import {useEffect, useState, useCallback, useMemo, useRef} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {
  getRenderHistory,
  downloadRenderItem,
  requestRender,
  deleteRender,
} from '../../common/api/generated/endpoints/renders/renders'
import type {RenderBatchResponse, RenderItemResponse} from '../../common/api/generated/model'
import {useToast} from '../../common/components/toast/toastContext'
import {handleApiError} from '../../common/errors/handleApiError'
import {trackEvent} from '../../common/analytics/trackEvent'
import {downloadFile} from '../../common/utils/downloadFile'
import {EXPORT_PRESETS} from '../../common/constants/exportPresets'
import LoadingLogo from '../components/LoadingLogo'
import Icon from '../components/renderHistory/Icon'
import ConfirmModal from '../components/myProjects/ConfirmModal'
import './MyProjectsPage.css' // reuses .mp-head, .mp-stats, .mp-stat
import './RenderHistoryPage.css'

// ── Constants ─────────────────────────────────────────────────────

/** Server-side retention before full PNG is pruned (mirrors `RENDER_RETENTION_MS` default). */
const RETENTION_DAYS = 30
const POLL_INTERVAL_MS = 15_000

type Platform = 'apple' | 'android'

/** Match exported W×H back to the canonical preset the user chose, returning its
 * label (e.g. "iPhone 6.7"", "Phone (9:16)") and platform for icon display. */
const lookupPreset = (width: number, height: number): {label: string; platform: Platform} | null => {
  const preset = EXPORT_PRESETS.find((p) => p.width === width && p.height === height)
  if (!preset) return null
  const platform: Platform = preset.group === 'Google Play' ? 'android' : 'apple'
  return {label: preset.label, platform}
}

// ── Time helpers ──────────────────────────────────────────────────

type RelativeKind = 'justNow' | 'minAgo' | 'hoursAgo' | 'yesterday' | 'daysAgo' | 'absolute'

interface Relative {
  kind: RelativeKind
  count?: number
  absolute?: string
}

/** Bucket an ISO timestamp into a relative-time descriptor. The component renders the i18n string. */
const computeRelative = (iso: string): Relative => {
  const then = new Date(iso).getTime()
  const diffMs = Math.max(0, Date.now() - then)
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return {kind: 'justNow'}
  if (minutes < 60) return {kind: 'minAgo', count: minutes}
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return {kind: 'hoursAgo', count: hours}
  const days = Math.floor(hours / 24)
  if (days === 1) return {kind: 'yesterday'}
  if (days < 30) return {kind: 'daysAgo', count: days}
  return {kind: 'absolute', absolute: new Date(iso).toLocaleDateString()}
}

/** Days remaining until expiry, derived from `createdAt + RETENTION` or `expiredAt`. */
const computeDaysLeft = (createdAt: string, expiredAt: string | null): number => {
  if (expiredAt) return 0
  const expiresAt = new Date(createdAt).getTime() + RETENTION_DAYS * 86_400_000
  const remaining = expiresAt - Date.now()
  return Math.ceil(remaining / 86_400_000)
}

/** Render a relative descriptor through i18n; kept inline so the i18n `t` type is preserved. */
const relativeLabel = (r: Relative, t: ReturnType<typeof useTranslation>['t']): string => {
  switch (r.kind) {
    case 'justNow':
      return t('renderHistory.relativeJustNow')
    case 'minAgo':
      return t('renderHistory.relativeMinAgo', {count: r.count})
    case 'hoursAgo':
      return t('renderHistory.relativeHoursAgo', {count: r.count})
    case 'yesterday':
      return t('renderHistory.relativeYesterday')
    case 'daysAgo':
      return t('renderHistory.relativeDaysAgo', {count: r.count})
    case 'absolute':
      return r.absolute ?? ''
  }
}

// ── Subcomponents ─────────────────────────────────────────────────

const ResolutionPill = ({width, height}: {width: number; height: number}) => {
  const match = lookupPreset(width, height)
  const resText = `${width} × ${height}`
  return (
    <span className={match ? 'rh-res' : 'rh-res rh-res-raw'}>
      {match && <Icon name={match.platform} size={11} />}
      {match?.label ?? resText}
      <span className="rh-tooltip" role="tooltip">
        {resText}
      </span>
    </span>
  )
}

const ExpiryPill = ({createdAt, expiredAt}: {createdAt: string; expiredAt: string | null}) => {
  const {t} = useTranslation()
  const days = computeDaysLeft(createdAt, expiredAt)
  let cls = ''
  let label: string
  if (days <= 0) {
    cls = 'urgent'
    label = t('renderHistory.expired')
  } else if (days === 1) {
    cls = 'warn'
    label = t('renderHistory.expiryOneDay')
  } else if (days <= 7) {
    cls = 'warn'
    label = t('renderHistory.expiryDaysLeft', {count: days})
  } else {
    label = t('renderHistory.expiryDaysLeft', {count: days})
  }
  return (
    <span className={`rh-expiry ${cls}`.trim()}>
      <Icon name="clock" size={11} /> {label}
    </span>
  )
}

interface CardProps {
  item: RenderItemResponse
  batchCreatedAt: string
  onDownload: (id: string) => void
  onRequest: (id: string) => void
  onDelete: (item: RenderItemResponse) => void
}

const RenderCard = ({item, batchCreatedAt, onDownload, onRequest, onDelete}: CardProps) => {
  const {t} = useTranslation()
  const expired = item.expiredAt !== null
  const isCompleted = item.status === 'COMPLETED' && !expired && (item.thumbnailUrl ?? item.renderUrl)
  const isFailed = item.status === 'FAILED'
  const isPending = item.status === 'PENDING' || item.status === 'PROCESSING'
  // Prepared but never requested — no render exists yet, user triggers on demand.
  const isSnapshot = item.status === 'SNAPSHOT' && !expired

  const sceneName = item.sceneName ?? t('renderHistory.fallbackSceneName', {n: item.sceneIndex + 1})

  const handleClickDownload = useCallback(() => onDownload(item.id), [item.id, onDownload])
  const handleClickRequest = useCallback(() => onRequest(item.id), [item.id, onRequest])
  const handleClickDelete = useCallback(() => onDelete(item), [item, onDelete])

  return (
    <div className={`rh-card${expired ? ' expired' : ''}`}>
      <div className="rh-thumb">
        {isCompleted ? (
          <img className="rh-thumb-img" src={item.thumbnailUrl ?? item.renderUrl ?? ''} alt={sceneName} />
        ) : isFailed ? (
          <div className="rh-thumb-fallback error" aria-label={t('renderHistory.statusFailed')}>
            !
          </div>
        ) : isPending ? (
          <div className="rh-thumb-fallback">
            <LoadingLogo size={28} />
          </div>
        ) : isSnapshot ? (
          <div className="rh-thumb-fallback rh-thumb-note">{t('renderHistory.renderingLongInfo')}</div>
        ) : (
          // expired or unknown
          <div className="rh-thumb-fallback" />
        )}
        <ResolutionPill width={item.exportWidth} height={item.exportHeight} />
        {!isPending && <ExpiryPill createdAt={batchCreatedAt} expiredAt={item.expiredAt} />}
      </div>
      <div className="rh-body">
        <div className="rh-scene-name" title={sceneName}>
          {sceneName}
        </div>
        <div className="rh-meta">
          <span>{relativeLabel(computeRelative(batchCreatedAt), t)}</span>
        </div>
        <div className="rh-actions">
          {expired ? (
            <span className="rh-expired-note">{t('renderHistory.noLongerAvailable')}</span>
          ) : isCompleted ? (
            <button className="rh-btn primary" type="button" onClick={handleClickDownload}>
              <Icon name="cloudDownload" size={13} /> {t('renderHistory.download')}
            </button>
          ) : isSnapshot ? (
            <button className="rh-btn primary" type="button" onClick={handleClickRequest}>
              <Icon name="render" size={14} /> {t('renderHistory.requestRender')}
            </button>
          ) : isFailed ? (
            <button className="rh-btn" type="button" onClick={handleClickRequest}>
              <Icon name="retry" size={14} /> {t('renderHistory.retry')}
            </button>
          ) : (
            <span className="rh-expired-note">{t('renderHistory.statusProcessing')}</span>
          )}
          <button
            className="rh-icon-btn"
            type="button"
            onClick={handleClickDelete}
            title={t('renderHistory.delete.short')}
            aria-label={t('renderHistory.delete.short')}
          >
            <Icon name="trash" size={15} />
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────

interface ProjectGroup {
  /** Stable group key — projectId when the project still exists, otherwise `__deleted:${name}`. */
  key: string
  projectId: string | null
  projectName: string
  items: Array<{item: RenderItemResponse; batchCreatedAt: string}>
}

const groupByProject = (batches: RenderBatchResponse[]): ProjectGroup[] => {
  const map = new Map<string, ProjectGroup>()
  for (const batch of batches) {
    const key = batch.projectId ?? `__deleted:${batch.projectName}`
    let group = map.get(key)
    if (!group) {
      group = {key, projectId: batch.projectId, projectName: batch.projectName, items: []}
      map.set(key, group)
    }
    for (const item of batch.items) {
      group.items.push({item, batchCreatedAt: batch.createdAt})
    }
  }
  // Items already arrive newest-first because batches are ordered desc; preserve that order.
  return Array.from(map.values())
}

const RenderHistoryPage = () => {
  const {t} = useTranslation()
  const toast = useToast()
  const [batches, setBatches] = useState<RenderBatchResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<RenderItemResponse | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const itemsHavePending = useCallback(
    (list: RenderBatchResponse[]) =>
      list.some((b) => b.items.some((i) => i.status === 'PENDING' || i.status === 'PROCESSING')),
    [],
  )

  const fetchData = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const resp = await getRenderHistory({limit: 20})
      setBatches(resp.data.batches)
    } catch (err) {
      toast.error(handleApiError(err, 'renderHistory.list') ?? t('renderHistory.loadError'))
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [t, toast])

  useEffect(() => {
    trackEvent('render_history_viewed')
    fetchData()
  }, [fetchData])

  // Poll while any item is still rendering.
  useEffect(() => {
    if (loading) return
    if (!itemsHavePending(batches)) {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
      return
    }
    if (pollRef.current) return
    pollRef.current = setInterval(async () => {
      try {
        const resp = await getRenderHistory({limit: 20})
        setBatches(resp.data.batches)
        if (!itemsHavePending(resp.data.batches) && pollRef.current) {
          clearInterval(pollRef.current)
          pollRef.current = null
        }
      } catch (err) {
        handleApiError(err, 'renderHistory.poll')
        // Polling — breadcrumb only, no toast spam
      }
    }, POLL_INTERVAL_MS)
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [loading, batches, itemsHavePending])

  const handleDownload = useCallback(
    async (itemId: string) => {
      try {
        const resp = await downloadRenderItem(itemId)
        // Fetch the presigned S3 URL into a Blob so the iOS download path
        // (Web Share / new-tab fallback) can be used uniformly with desktop.
        const fileResp = await fetch(resp.data.url)
        const blob = await fileResp.blob()
        await downloadFile(blob, `render-${itemId}.png`)
        trackEvent('render_history_download', {item_id: itemId})
      } catch (err) {
        toast.error(handleApiError(err, 'renderHistory.download') ?? t('renderHistory.downloadError'))
      }
    },
    [t, toast],
  )

  // Optimistically patch a single item's status (used before request/retry so the
  // poll effect starts and the UI reflects the pending render immediately).
  const setItemStatus = useCallback((itemId: string, status: string) => {
    setBatches((prev) => prev.map((b) => ({...b, items: b.items.map((i) => (i.id === itemId ? {...i, status} : i))})))
  }, [])

  // Request (or retry) a render on demand. Serves both the SNAPSHOT "Request
  // render" and the FAILED "Retry" — the BE endpoint resets the attempt counter.
  const handleRequest = useCallback(
    async (itemId: string) => {
      let prior: string | undefined
      for (const b of batches) {
        const found = b.items.find((i) => i.id === itemId)
        if (found) {
          prior = found.status
          break
        }
      }
      setItemStatus(itemId, 'PENDING')
      try {
        await requestRender(itemId)
        trackEvent(prior === 'FAILED' ? 'render_history_retry' : 'render_history_request', {item_id: itemId})
      } catch (err) {
        if (prior) setItemStatus(itemId, prior)
        toast.error(handleApiError(err, 'renderHistory.request') ?? t('renderHistory.requestError'))
      }
    },
    [batches, setItemStatus, t, toast],
  )

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return
    const id = deleteTarget.id
    setDeleteTarget(null)
    // Optimistic removal; drop any batch group that becomes empty.
    setBatches((prev) =>
      prev.map((b) => ({...b, items: b.items.filter((i) => i.id !== id)})).filter((b) => b.items.length > 0),
    )
    try {
      await deleteRender(id)
      trackEvent('render_history_delete_confirmed', {item_id: id})
    } catch (err) {
      toast.error(handleApiError(err, 'renderHistory.delete') ?? t('renderHistory.deleteError'))
      fetchData() // re-sync on failure
    }
  }, [deleteTarget, fetchData, t, toast])

  const groups = useMemo(() => groupByProject(batches), [batches])

  // Stat counters — derived from flat items.
  const {total, available, expired} = useMemo(() => {
    let totalC = 0
    let availC = 0
    let expC = 0
    for (const b of batches) {
      for (const item of b.items) {
        totalC += 1
        if (item.expiredAt !== null) expC += 1
        else if (item.status === 'COMPLETED') availC += 1
      }
    }
    return {total: totalC, available: availC, expired: expC}
  }, [batches])

  return (
    <div className="render-history-root">
      <div className="bg-grid" />
      <div className="noise" />

      <main className="projects-page">
        <header className="mp-head">
          <span className="mp-eyebrow">{t('renderHistory.eyebrow')}</span>
          <div className="mp-head-row">
            <div>
              <h1 className="mp-title">{t('renderHistory.title')}</h1>
              <p className="mp-sub">{t('renderHistory.subtitle')}</p>
            </div>
          </div>
        </header>

        <div className="mp-stats rh-stats">
          <div className="mp-stat">
            <span className="mp-stat-label">{t('renderHistory.statTotal')}</span>
            <span className="mp-stat-value">{total}</span>
          </div>
          <div className="mp-stat">
            <span className="mp-stat-label">{t('renderHistory.statAvailable')}</span>
            <span className="mp-stat-value">{available}</span>
          </div>
          <div className="mp-stat">
            <span className="mp-stat-label">{t('renderHistory.statExpired')}</span>
            <span className="mp-stat-value">{expired}</span>
          </div>
        </div>

        {loading ? (
          <div className="rh-cards">
            {Array.from({length: 4}).map((_, i) => (
              <div key={i} className="rh-skeleton-card">
                <div className="rh-skeleton-thumb" />
                <div className="rh-skeleton-line" style={{width: '70%'}} />
                <div className="rh-skeleton-line" style={{width: '40%'}} />
              </div>
            ))}
          </div>
        ) : loadError && groups.length === 0 ? (
          <div className="rh-empty">
            <h3>{t('renderHistory.loadError')}</h3>
            <button type="button" className="mp-empty-cta" onClick={fetchData}>
              {t('projects.retry')}
            </button>
          </div>
        ) : groups.length === 0 ? (
          <div className="rh-empty">
            <h3>{t('renderHistory.empty.title')}</h3>
            <p>{t('renderHistory.empty.body')}</p>
          </div>
        ) : (
          groups.map((group) => (
            <section className="rh-group" key={group.key}>
              <div className="rh-group-head">
                <div className="rh-group-title">
                  <h2>{group.projectName}</h2>
                  <span className="rh-group-count">{t('renderHistory.renderCount', {count: group.items.length})}</span>
                </div>
                {group.projectId ? (
                  <Link className="rh-group-cta" to={`/editor/${group.projectId}`}>
                    {t('renderHistory.openProject')} <Icon name="arrow" size={13} />
                  </Link>
                ) : (
                  <span className="rh-group-warn">
                    <Icon name="warn" size={13} /> {t('renderHistory.projectDeleted')}
                    <span className="rh-tooltip" role="tooltip">
                      {t('renderHistory.projectDeletedHelp')}
                    </span>
                  </span>
                )}
              </div>
              <div className="rh-cards">
                {group.items.map(({item, batchCreatedAt}) => (
                  <RenderCard
                    key={item.id}
                    item={item}
                    batchCreatedAt={batchCreatedAt}
                    onDownload={handleDownload}
                    onRequest={handleRequest}
                    onDelete={setDeleteTarget}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </main>

      <ConfirmModal
        open={!!deleteTarget}
        title={t('renderHistory.delete.title')}
        body={<p>{t('renderHistory.delete.body')}</p>}
        confirmLabel={t('renderHistory.delete.confirm')}
        cancelLabel={t('renderHistory.delete.cancel')}
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  )
}

export default RenderHistoryPage
